import { useEffect, useState } from 'react';
import useOrganizationConfig from 'src/api/core/organization-config/useOrganizationConfig';
import useUpdateOrganizationConfig from 'src/api/core/organization-config/useUpdateOrganizationConfig';

import {
  ShoppingCartIcon,
  TagIcon,
  GearIcon,
  FileTextIcon,
  ArrowsLeftRightIcon,
  BoatIcon,
  ReceiptIcon,
  XIcon,
  CpuIcon,
} from '@phosphor-icons/react';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import {
  useGetModuleConfigs,
  useCreateOrUpdateModuleConfig,
} from '@/api/org/config/modules';
import {
  ProductModule,
  AIWorkflow,
  RegisteredEmailDomain,
  RegisteredEmailHeaderOption,
} from '@erp/shared-types';

export const ModulesConfig: React.FC = () => {
  const { data: orgConfigData, isLoading: isLoadingConfig } =
    useOrganizationConfig();
  const { mutate: updateOrganizationConfig, isLoading: isUpdating } =
    useUpdateOrganizationConfig();

  const [enabledModules, setEnabledModules] = useState<string[]>([]);
  const [emailDomainDialogOpen, setEmailDomainDialogOpen] = useState(false);
  const [selectedModule, setSelectedModule] = useState<ProductModule | null>(
    null,
  );
  const [selectedAIWorkflow, setSelectedAIWorkflow] =
    useState<AIWorkflow | null>(null);
  const [newEmailDomain, setNewEmailDomain] = useState('');
  const [newEmailHeader, setNewEmailHeader] =
    useState<RegisteredEmailHeaderOption>('to');

  const { data: moduleConfigData } = useGetModuleConfigs();

  const { mutate: updateModuleConfig, isPending: isUpdatingModuleConfig } =
    useCreateOrUpdateModuleConfig();

  // Initialize enabled modules from API data
  useEffect(() => {
    if (orgConfigData?.organizationConfig?.enabledModules) {
      setEnabledModules(orgConfigData.organizationConfig.enabledModules);
    }
  }, [orgConfigData]);

  const isModuleEnabled = (moduleName: string) =>
    enabledModules.includes(moduleName);

  const handleToggleModule = (moduleName: string) => {
    const originalModules = enabledModules; // Capture original state before any changes
    const updatedModules = isModuleEnabled(moduleName)
      ? enabledModules.filter((module) => module !== moduleName)
      : [...enabledModules, moduleName];

    // Optimistically update local state
    setEnabledModules(updatedModules);

    // Update backend
    updateOrganizationConfig(
      { enabledModules: updatedModules },
      {
        onError: () => {
          // Revert to original state
          setEnabledModules(originalModules);
        },
      },
    );
  };

  // Get email domains for a specific module and workflow
  const getEmailDomains = (
    module: ProductModule,
    workflow: AIWorkflow,
  ): RegisteredEmailDomain[] => {
    const moduleConfig = moduleConfigData?.find(
      (config) => config.module === module,
    );
    return (
      moduleConfig?.config?.emailWorkflows?.[workflow]
        ?.registeredEmailDomains || []
    );
  };

  // Open email domains dialog
  const handleOpenEmailDomainsDialog = (
    module: ProductModule,
    workflow: AIWorkflow,
  ) => {
    setSelectedModule(module);
    setSelectedAIWorkflow(workflow);
    setEmailDomainDialogOpen(true);
  };

  // Add new email domain
  const handleAddEmailDomain = () => {
    if (!selectedModule || !selectedAIWorkflow || !newEmailDomain.trim())
      return;

    const currentDomains = getEmailDomains(selectedModule, selectedAIWorkflow);
    const newDomain: RegisteredEmailDomain = {
      header: newEmailHeader,
      domain: newEmailDomain.trim(),
    };

    const updatedDomains = [...currentDomains, newDomain];
    const existing = moduleConfigData?.find((c) => c.module === selectedModule);
    const existingWorkflows = existing?.config?.emailWorkflows ?? {};
    const existingForWorkflow =
      (selectedAIWorkflow && existingWorkflows[selectedAIWorkflow]) ?? {};
    updateModuleConfig(
      {
        module: selectedModule,
        config: {
          emailWorkflows: {
            ...existingWorkflows,
            [selectedAIWorkflow]: {
              ...existingForWorkflow,
              registeredEmailDomains: updatedDomains,
            },
          },
        },
      },
      {
        onSuccess: () => {
          setNewEmailDomain('');
          setNewEmailHeader('to');
        },
      },
    );
  };

  // Remove email domain
  const handleRemoveEmailDomain = (
    module: ProductModule,
    workflow: AIWorkflow,
    index: number,
  ) => {
    const currentDomains = getEmailDomains(module, workflow);
    const updatedDomains = currentDomains.filter((_, i) => i !== index);

    const existing = moduleConfigData?.find((c) => c.module === module);
    const existingWorkflows = existing?.config?.emailWorkflows ?? {};
    const existingForWorkflow = existingWorkflows[workflow] ?? {};
    updateModuleConfig({
      module,
      config: {
        emailWorkflows: {
          ...existingWorkflows,
          [workflow]: {
            ...existingForWorkflow,
            registeredEmailDomains: updatedDomains,
          },
        },
      },
    });
  };

  // If still loading initial config, show loading state
  if (isLoadingConfig) {
    return (
      <div className="p-6">
        <div className="text-lg font-medium text-muted-foreground">
          Loading modules configuration...
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <div
            className={`flex h-12 w-12 items-center justify-center rounded-lg ${
              isModuleEnabled(ProductModule.ORDER_MANAGEMENT)
                ? 'bg-blue-100 dark:bg-blue-900/20'
                : 'bg-gray-100 dark:bg-gray-800/20'
            }`}
          >
            <ShoppingCartIcon
              className={`h-6 w-6 ${
                isModuleEnabled(ProductModule.ORDER_MANAGEMENT)
                  ? 'text-blue-600 dark:text-blue-400'
                  : 'text-gray-400 dark:text-gray-600'
              }`}
            />
          </div>
          <div>
            <h3 className="text-lg font-semibold">Order Management</h3>
            <p className="text-sm text-muted-foreground">
              Manage sales and purchase orders
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-muted-foreground">
            {isModuleEnabled(ProductModule.ORDER_MANAGEMENT)
              ? 'Enabled'
              : 'Disabled'}
          </span>
          <Switch
            checked={isModuleEnabled(ProductModule.ORDER_MANAGEMENT)}
            onCheckedChange={() =>
              handleToggleModule(ProductModule.ORDER_MANAGEMENT)
            }
            disabled={isUpdating}
          />
        </div>
      </div>

      {/* Email Domains Configuration for Order Management */}
      {isModuleEnabled(ProductModule.ORDER_MANAGEMENT) && (
        <div className="ml-16 space-y-4">
          <div className="flex justify-between items-center">
            <h4 className="text-base font-medium">
              Email Workflow Configuration
            </h4>
          </div>

          {/* Sales Order Draft Emails */}
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <p className="text-sm text-muted-foreground">
                Sales Order Draft Email Domains
              </p>
              <Button
                size="sm"
                variant="outline"
                onClick={() =>
                  handleOpenEmailDomainsDialog(
                    ProductModule.ORDER_MANAGEMENT,
                    AIWorkflow.EMAIL_SO_INGESTION,
                  )
                }
                disabled={isUpdatingModuleConfig}
              >
                Configure
              </Button>
            </div>
            <div className="flex flex-wrap gap-2">
              {getEmailDomains(
                ProductModule.ORDER_MANAGEMENT,
                AIWorkflow.EMAIL_SO_INGESTION,
              ).map((domain, index) => (
                <Badge
                  key={index}
                  variant="secondary"
                  className="flex items-center gap-1"
                >
                  {domain.header}: {domain.domain}
                  <button
                    onClick={() =>
                      handleRemoveEmailDomain(
                        ProductModule.ORDER_MANAGEMENT,
                        AIWorkflow.EMAIL_SO_INGESTION,
                        index,
                      )
                    }
                    disabled={isUpdatingModuleConfig}
                    className="ml-1 hover:bg-destructive hover:text-destructive-foreground rounded-full p-0.5"
                  >
                    <XIcon className="h-3 w-3" />
                  </button>
                </Badge>
              ))}
              {getEmailDomains(
                ProductModule.ORDER_MANAGEMENT,
                AIWorkflow.EMAIL_SO_INGESTION,
              ).length === 0 && (
                <p className="text-xs text-muted-foreground">
                  No email domains configured
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      <Separator />

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <div
            className={`flex h-12 w-12 items-center justify-center rounded-lg ${
              isModuleEnabled(ProductModule.REBATES)
                ? 'bg-green-100 dark:bg-green-900/20'
                : 'bg-gray-100 dark:bg-gray-800/20'
            }`}
          >
            <TagIcon
              className={`h-6 w-6 ${
                isModuleEnabled(ProductModule.REBATES)
                  ? 'text-green-600 dark:text-green-400'
                  : 'text-gray-400 dark:text-gray-600'
              }`}
            />
          </div>
          <div>
            <h3 className="text-lg font-semibold">Rebates</h3>
            <p className="text-sm text-muted-foreground">
              Manage rebate agreements and calculations
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-muted-foreground">
            {isModuleEnabled(ProductModule.REBATES) ? 'Enabled' : 'Disabled'}
          </span>
          <Switch
            checked={isModuleEnabled(ProductModule.REBATES)}
            onCheckedChange={() => handleToggleModule(ProductModule.REBATES)}
            disabled={isUpdating}
          />
        </div>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <div
            className={`flex h-12 w-12 items-center justify-center rounded-lg ${
              isModuleEnabled(ProductModule.FAMOUS_INTEGRATION)
                ? 'bg-orange-100 dark:bg-orange-900/20'
                : 'bg-gray-100 dark:bg-gray-800/20'
            }`}
          >
            <GearIcon
              className={`h-6 w-6 ${
                isModuleEnabled(ProductModule.FAMOUS_INTEGRATION)
                  ? 'text-orange-600 dark:text-orange-400'
                  : 'text-gray-400 dark:text-gray-600'
              }`}
            />
          </div>
          <div>
            <h3 className="text-lg font-semibold">Famous Integration</h3>
            <p className="text-sm text-muted-foreground">
              Connect with Famous ERP system
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-muted-foreground">
            {isModuleEnabled(ProductModule.FAMOUS_INTEGRATION)
              ? 'Enabled'
              : 'Disabled'}
          </span>
          <Switch
            checked={isModuleEnabled(ProductModule.FAMOUS_INTEGRATION)}
            onCheckedChange={() =>
              handleToggleModule(ProductModule.FAMOUS_INTEGRATION)
            }
            disabled={isUpdating}
          />
        </div>
      </div>

      <Separator />

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <div
            className={`flex h-12 w-12 items-center justify-center rounded-lg ${
              isModuleEnabled(ProductModule.PROCUREMENT)
                ? 'bg-indigo-100 dark:bg-indigo-900/20'
                : 'bg-gray-100 dark:bg-gray-800/20'
            }`}
          >
            <FileTextIcon
              className={`h-6 w-6 ${
                isModuleEnabled(ProductModule.PROCUREMENT)
                  ? 'text-indigo-600 dark:text-indigo-400'
                  : 'text-gray-400 dark:text-gray-600'
              }`}
            />
          </div>
          <div>
            <h3 className="text-lg font-semibold">Procurement</h3>
            <p className="text-sm text-muted-foreground">
              Manage sourcing and vendor relationships
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-muted-foreground">
            {isModuleEnabled(ProductModule.PROCUREMENT)
              ? 'Enabled'
              : 'Disabled'}
          </span>
          <Switch
            checked={isModuleEnabled(ProductModule.PROCUREMENT)}
            onCheckedChange={() =>
              handleToggleModule(ProductModule.PROCUREMENT)
            }
            disabled={isUpdating}
          />
        </div>
      </div>

      {/* Email Domains Configuration for Procurement */}
      {isModuleEnabled(ProductModule.PROCUREMENT) && (
        <div className="ml-16 space-y-4">
          <div className="flex justify-between items-center">
            <h4 className="text-base font-medium">
              Email Workflow Configuration
            </h4>
          </div>

          {/* Quote Sheet Emails */}
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <p className="text-sm text-muted-foreground">
                Quote Sheet Email Domains
              </p>
              <Button
                size="sm"
                variant="outline"
                onClick={() =>
                  handleOpenEmailDomainsDialog(
                    ProductModule.PROCUREMENT,
                    AIWorkflow.PRICE_SHEET_INGESTION,
                  )
                }
                disabled={isUpdatingModuleConfig}
              >
                Configure
              </Button>
            </div>
            <div className="flex flex-wrap gap-2">
              {getEmailDomains(
                ProductModule.PROCUREMENT,
                AIWorkflow.PRICE_SHEET_INGESTION,
              ).map((domain, index) => (
                <Badge
                  key={index}
                  variant="secondary"
                  className="flex items-center gap-1"
                >
                  {domain.header}: {domain.domain}
                  <button
                    onClick={() =>
                      handleRemoveEmailDomain(
                        ProductModule.PROCUREMENT,
                        AIWorkflow.PRICE_SHEET_INGESTION,
                        index,
                      )
                    }
                    disabled={isUpdatingModuleConfig}
                    className="ml-1 hover:bg-destructive hover:text-destructive-foreground rounded-full p-0.5"
                  >
                    <XIcon className="h-3 w-3" />
                  </button>
                </Badge>
              ))}
              {getEmailDomains(
                ProductModule.PROCUREMENT,
                AIWorkflow.PRICE_SHEET_INGESTION,
              ).length === 0 && (
                <p className="text-xs text-muted-foreground">
                  No email domains configured
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      <Separator />

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <div
            className={`flex h-12 w-12 items-center justify-center rounded-lg ${
              isModuleEnabled(ProductModule.ORDERFUL_EDI)
                ? 'bg-teal-100 dark:bg-teal-900/20'
                : 'bg-gray-100 dark:bg-gray-800/20'
            }`}
          >
            <ArrowsLeftRightIcon
              className={`h-6 w-6 ${
                isModuleEnabled(ProductModule.ORDERFUL_EDI)
                  ? 'text-teal-600 dark:text-teal-400'
                  : 'text-gray-400 dark:text-gray-600'
              }`}
            />
          </div>
          <div>
            <h3 className="text-lg font-semibold">EDI</h3>
            <p className="text-sm text-muted-foreground">
              Electronic Data Interchange for seamless order processing
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-muted-foreground">
            {isModuleEnabled(ProductModule.ORDERFUL_EDI)
              ? 'Enabled'
              : 'Disabled'}
          </span>
          <Switch
            checked={isModuleEnabled(ProductModule.ORDERFUL_EDI)}
            onCheckedChange={() =>
              handleToggleModule(ProductModule.ORDERFUL_EDI)
            }
            disabled={isUpdating}
          />
        </div>
      </div>

      <Separator />

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <div
            className={`flex h-12 w-12 items-center justify-center rounded-lg ${
              isModuleEnabled(ProductModule.SHIP_AND_DEBIT)
                ? 'bg-cyan-100 dark:bg-cyan-900/20'
                : 'bg-gray-100 dark:bg-gray-800/20'
            }`}
          >
            <BoatIcon
              className={`h-6 w-6 ${
                isModuleEnabled(ProductModule.SHIP_AND_DEBIT)
                  ? 'text-cyan-600 dark:text-cyan-400'
                  : 'text-gray-400 dark:text-gray-600'
              }`}
            />
          </div>
          <div>
            <h3 className="text-lg font-semibold">Ship and Debit</h3>
            <p className="text-sm text-muted-foreground">
              Manage 844 transactions and vendor debit claims
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-muted-foreground">
            {isModuleEnabled(ProductModule.SHIP_AND_DEBIT)
              ? 'Enabled'
              : 'Disabled'}
          </span>
          <Switch
            checked={isModuleEnabled(ProductModule.SHIP_AND_DEBIT)}
            onCheckedChange={() =>
              handleToggleModule(ProductModule.SHIP_AND_DEBIT)
            }
            disabled={isUpdating}
          />
        </div>
      </div>

      <Separator />

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <div
            className={`flex h-12 w-12 items-center justify-center rounded-lg ${
              isModuleEnabled(ProductModule.AP_INVOICING)
                ? 'bg-red-100 dark:bg-red-900/20'
                : 'bg-gray-100 dark:bg-gray-800/20'
            }`}
          >
            <ReceiptIcon
              className={`h-6 w-6 ${
                isModuleEnabled(ProductModule.AP_INVOICING)
                  ? 'text-red-600 dark:text-red-400'
                  : 'text-gray-400 dark:text-gray-600'
              }`}
            />
          </div>
          <div>
            <h3 className="text-lg font-semibold">AP Invoicing</h3>
            <p className="text-sm text-muted-foreground">
              Accounts Payable invoice processing and management
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-muted-foreground">
            {isModuleEnabled(ProductModule.AP_INVOICING)
              ? 'Enabled'
              : 'Disabled'}
          </span>
          <Switch
            checked={isModuleEnabled(ProductModule.AP_INVOICING)}
            onCheckedChange={() =>
              handleToggleModule(ProductModule.AP_INVOICING)
            }
            disabled={isUpdating}
          />
        </div>
      </div>

      {/* Email Domains Configuration for AP Invoicing */}
      {isModuleEnabled(ProductModule.AP_INVOICING) && (
        <div className="ml-16 space-y-4">
          <div className="flex justify-between items-center">
            <h4 className="text-base font-medium">
              Email Workflow Configuration
            </h4>
          </div>

          {/* AP Invoice Emails */}
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <p className="text-sm text-muted-foreground">
                AP Invoice Email Domains
              </p>
              <Button
                size="sm"
                variant="outline"
                onClick={() =>
                  handleOpenEmailDomainsDialog(
                    ProductModule.AP_INVOICING,
                    AIWorkflow.AP_INVOICE_INGESTION,
                  )
                }
                disabled={isUpdatingModuleConfig}
              >
                Configure
              </Button>
            </div>
            <div className="flex flex-wrap gap-2">
              {getEmailDomains(
                ProductModule.AP_INVOICING,
                AIWorkflow.AP_INVOICE_INGESTION,
              ).map((domain, index) => (
                <Badge
                  key={index}
                  variant="secondary"
                  className="flex items-center gap-1"
                >
                  {domain.header}: {domain.domain}
                  <button
                    onClick={() =>
                      handleRemoveEmailDomain(
                        ProductModule.AP_INVOICING,
                        AIWorkflow.AP_INVOICE_INGESTION,
                        index,
                      )
                    }
                    disabled={isUpdatingModuleConfig}
                    className="ml-1 hover:bg-destructive hover:text-destructive-foreground rounded-full p-0.5"
                  >
                    <XIcon className="h-3 w-3" />
                  </button>
                </Badge>
              ))}
              {getEmailDomains(
                ProductModule.AP_INVOICING,
                AIWorkflow.AP_INVOICE_INGESTION,
              ).length === 0 && (
                <p className="text-xs text-muted-foreground">
                  No email domains configured
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      <Separator />

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <div
            className={`flex h-12 w-12 items-center justify-center rounded-lg ${
              isModuleEnabled(ProductModule.PROCUREMENT_DEMO)
                ? 'bg-violet-100 dark:bg-violet-900/20'
                : 'bg-gray-100 dark:bg-gray-800/20'
            }`}
          >
            <CpuIcon
              className={`h-6 w-6 ${
                isModuleEnabled(ProductModule.PROCUREMENT_DEMO)
                  ? 'text-violet-600 dark:text-violet-400'
                  : 'text-gray-400 dark:text-gray-600'
              }`}
            />
          </div>
          <div>
            <h3 className="text-lg font-semibold">Procurement</h3>
            <p className="text-sm text-muted-foreground">
              AI-powered procurement and vendor management demo
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-muted-foreground">
            {isModuleEnabled(ProductModule.PROCUREMENT_DEMO)
              ? 'Enabled'
              : 'Disabled'}
          </span>
          <Switch
            checked={isModuleEnabled(ProductModule.PROCUREMENT_DEMO)}
            onCheckedChange={() =>
              handleToggleModule(ProductModule.PROCUREMENT_DEMO)
            }
            disabled={isUpdating}
          />
        </div>
      </div>

      {/* Email Domain Configuration Dialog */}
      <Dialog
        open={emailDomainDialogOpen}
        onOpenChange={setEmailDomainDialogOpen}
      >
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Configure Email Domains</DialogTitle>
            {selectedAIWorkflow && (
              <p className="text-sm text-muted-foreground">
                {(() => {
                  switch (selectedAIWorkflow) {
                    case AIWorkflow.EMAIL_SO_INGESTION:
                      return 'Sales Order Draft Emails';
                    case AIWorkflow.PRICE_SHEET_INGESTION:
                      return 'Quote Sheet Emails';
                    case AIWorkflow.AP_INVOICE_INGESTION:
                      return 'AP Invoice Emails';
                    default:
                      return 'Email Configuration';
                  }
                })()}
              </p>
            )}
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <p className="text-sm font-medium mb-2">Current domains:</p>
              <div className="flex flex-wrap gap-2">
                {selectedModule &&
                  selectedAIWorkflow &&
                  getEmailDomains(selectedModule, selectedAIWorkflow).map(
                    (domain, index) => (
                      <Badge
                        key={index}
                        variant="secondary"
                        className="flex items-center gap-1"
                      >
                        {domain.header}: {domain.domain}
                        <button
                          onClick={() => {
                            if (selectedModule && selectedAIWorkflow) {
                              handleRemoveEmailDomain(
                                selectedModule,
                                selectedAIWorkflow,
                                index,
                              );
                            }
                          }}
                          disabled={isUpdatingModuleConfig}
                          className="ml-1 hover:bg-destructive hover:text-destructive-foreground rounded-full p-0.5"
                        >
                          <XIcon className="h-3 w-3" />
                        </button>
                      </Badge>
                    ),
                  )}
                {selectedModule &&
                  selectedAIWorkflow &&
                  getEmailDomains(selectedModule, selectedAIWorkflow).length ===
                    0 && (
                    <p className="text-xs text-muted-foreground">
                      No email domains configured
                    </p>
                  )}
              </div>
            </div>

            <div>
              <h4 className="text-sm font-medium mb-3">Add New Domain</h4>
              <div className="flex gap-2 items-end">
                <div className="w-20">
                  <Label htmlFor="header-select" className="text-xs">
                    Header
                  </Label>
                  <Select
                    value={newEmailHeader}
                    onValueChange={(value) =>
                      setNewEmailHeader(value as RegisteredEmailHeaderOption)
                    }
                  >
                    <SelectTrigger id="header-select" size="sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="to">To</SelectItem>
                      <SelectItem value="cc">CC</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex-1">
                  <Label htmlFor="domain-input" className="text-xs">
                    Domain
                  </Label>
                  <Input
                    id="domain-input"
                    value={newEmailDomain}
                    onChange={(e) => setNewEmailDomain(e.target.value)}
                    placeholder="example.com"
                  />
                </div>
                <Button
                  onClick={handleAddEmailDomain}
                  disabled={!newEmailDomain.trim() || isUpdatingModuleConfig}
                  size="sm"
                >
                  Add
                </Button>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setEmailDomainDialogOpen(false)}
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
